// Anlage des Schadens aus der Schadenmappe (Spec §1): Sobald Kontakt und
// Schadenart feststehen, wird der Schaden genau einmal angelegt. Fehler bleiben
// stehen (Eingaben bleiben erhalten), bis `wiederholen()` gerufen wird.
import * as React from "react";
import type { BigAnswers } from "../../schadenmeldung-big/types";
import type { createDamage } from "../api";
import type { MappeDamage } from "../types";

export type AnlageZustand = "idle" | "laeuft" | "fehler";

/** Der Stand, den der POST der Anlage geschickt hat. */
export interface AnlageGesendet {
  answers: BigAnswers;
  schadensart: string | null;
}

export function useSchadenAnlage(opts: {
  /** Nur in der Neuanlage und solange noch kein Schaden entstanden ist. */
  aktiv: boolean;
  kontaktId: number | null;
  risikoId: number | null;
  vertragId: number | null;
  answers: BigAnswers;
  schadensart: string | null;
  /** = `buildDescription(answers)`; erst beim Anlegen ausgewertet. */
  description: () => string;
  anlegen: typeof createDamage;
  /** `gesendet` = genau der gePOSTete Stand (Basis für den Autosave). */
  onAngelegt: (damage: MappeDamage, gesendet: AnlageGesendet) => void;
  /**
   * Kennung der Neuanlage-Sitzung. Ändert sie sich, beginnt die Anlage von
   * vorn: `erledigt`, `laeuft` und ein Fehler werden zurückgesetzt, Ergebnisse
   * der vorigen Sitzung verworfen.
   */
  schluessel?: unknown;
}): { zustand: AnlageZustand; fehler: string | null; wiederholen: () => void } {
  const [zustand, setZustand] = React.useState<AnlageZustand>("idle");
  const [fehler, setFehler] = React.useState<string | null>(null);
  /** Erhöht durch `wiederholen()` — löst nach einem Fehler den nächsten Versuch aus. */
  const [versuch, setVersuch] = React.useState(0);

  // Jüngste Werte für den Aufruf — die Auslöser stehen unten in den Deps.
  const optsRef = React.useRef(opts);
  optsRef.current = opts;

  const laeuft = React.useRef(false);
  const erledigt = React.useRef(false);
  const gescheitert = React.useRef(false);
  /** Erhöht bei jedem Sitzungswechsel — ältere Ergebnisse werden verworfen. */
  const generation = React.useRef(0);
  /** Ergebnisse nach dem Verlassen/Deaktivieren werden verworfen. */
  const aktivRef = React.useRef(false);
  React.useEffect(() => {
    aktivRef.current = opts.aktiv;
    return () => {
      aktivRef.current = false;
    };
  }, [opts.aktiv]);

  // Neue Sitzung: von vorn (vor dem Auslöse-Effekt, damit er im selben Commit greift).
  const schluesselRef = React.useRef(opts.schluessel);
  React.useEffect(() => {
    if (Object.is(schluesselRef.current, opts.schluessel)) return;
    schluesselRef.current = opts.schluessel;
    generation.current += 1;
    laeuft.current = false;
    erledigt.current = false;
    gescheitert.current = false;
    setZustand("idle");
    setFehler(null);
  }, [opts.schluessel]);

  const bereit = opts.aktiv && opts.kontaktId !== null && opts.answers.damageType !== null;

  React.useEffect(() => {
    if (!bereit || laeuft.current || erledigt.current || gescheitert.current) return;
    const o = optsRef.current;
    if (o.kontaktId === null) return;
    const gen = generation.current;
    const gesendet: AnlageGesendet = { answers: o.answers, schadensart: o.schadensart };
    laeuft.current = true;
    setZustand("laeuft");
    setFehler(null);
    o.anlegen({
      contact: { id: o.kontaktId },
      riskObject: o.risikoId !== null ? { id: o.risikoId } : null,
      contract: o.vertragId !== null ? { id: o.vertragId } : null,
      meldung: { ...gesendet.answers, schadensart: gesendet.schadensart },
      description: o.description(),
    }).then(
      (damage) => {
        if (gen !== generation.current) return; // vorige Sitzung
        laeuft.current = false;
        if (!aktivRef.current) return;
        erledigt.current = true;
        setZustand("idle");
        optsRef.current.onAngelegt(damage, gesendet);
      },
      (e: unknown) => {
        if (gen !== generation.current) return;
        laeuft.current = false;
        if (!aktivRef.current) return;
        gescheitert.current = true;
        setZustand("fehler");
        setFehler(e instanceof Error ? e.message : String(e));
      },
    );
  }, [bereit, versuch, opts.schluessel]);

  const wiederholen = React.useCallback(() => {
    if (laeuft.current || erledigt.current) return;
    gescheitert.current = false;
    setVersuch((n) => n + 1);
  }, []);

  return { zustand, fehler, wiederholen };
}
